import { WorkflowService } from '../workflow/services/workflow.service';
import {
    Component,
    Input,
    OnInit,
    ViewChildren
} from '@angular/core';
import { map } from 'rxjs/operators';
import { TaskType } from '../tasks/models';
import { WorkflowVocabService } from '../workflow/services/workflow-vocab.service';
import { NgModel } from '@angular/forms';
import { dateControlValidator } from '@common/util/date-control.validator';
import { updateEntity } from '../entity-changes/update-entity';
import { TaskInstanceService } from '@services/task-instance.service';
import { ResourceService } from '../resources';
import { Entity, TaskInstance, cv_TaskStatus, cv_TaskType } from '@common/types';
import { IFacet } from '@common/facet';
import { Appointment } from './models';
import { ScheduleHelperService } from './schedule-helper.service';
import { AuthService } from '@services/auth.service';

@Component({
    selector: 'schedule-detail',
    styles: [`
        .no-materials-warning-alert {
            margin-bottom: 12px;
        }
    `],
    templateUrl: './schedule-detail.component.html'
})
export class ScheduleDetailComponent implements OnInit {
    @ViewChildren('dateControl') dateControls: NgModel[];
    @Input() facet: IFacet;
    @Input() task: Entity<TaskInstance>;
    @Input() appointment: Appointment;
    @Input() taskType: TaskType;

    // CVs
    taskStatuses: cv_TaskStatus[] = [];
    taskTypes: cv_TaskType[] = [];
    currentResourceKey: number | null;

    // export enum to template
    TaskType = TaskType;

    loading = false;

    isReadOnly = true;

    readonly COMPONENT_LOG_TAG = 'schedule-detail';


    constructor(
        private workflowService: WorkflowService,
        private workflowVocabService: WorkflowVocabService,
        private resourceService: ResourceService,
        private taskInstanceService: TaskInstanceService,
        private scheduleHelperService: ScheduleHelperService,
        private authService: AuthService
    ) { }

    // lifecycle
    ngOnInit(): void {
        this.initialize();
    }

    initialize(): Promise<void> {
        this.isReadOnly = this.task.IsLocked || this.task.IsGroup || this.facet.Privilege === "ReadOnly"

        return this.getCVs().then(() => {
            return this.loadTaskData();
        }).catch((err) => {
            throw err;
        });
    }

    getCVs(): Promise<any> {
        const p1 = this.workflowVocabService.taskStatuses$.pipe(map((data) => {
            this.taskStatuses = data;
        })).toPromise();

        const p2 = this.workflowVocabService.taskTypes$.pipe(map((data) => {
            this.taskTypes = data;
        })).toPromise();

        const p3 = this.resourceService.getCurrentUserResource().then((resource) => {
            this.currentResourceKey = resource ? resource.C_Resource_key : null;
        })

        return Promise.all([p1, p2, p3]);
    }

    loadTaskData(): Promise<any> {
        if (!this.task) {
            return Promise.resolve();
        }

        return this.workflowService.ensureTaskRelationshipsExpanded([this.task]);
    }

    // <select> formatters
    taskStatusKeyFormatter = (value: cv_TaskStatus): number => {
        return value.C_TaskStatus_key;
    }
    taskStatusFormatter = (value: cv_TaskStatus): string => {
        return value.TaskStatus;
    }

    validate(): string {
        return dateControlValidator(this.dateControls);
    }

    // Due Date
    async dueDateChanged(date: Date): Promise<void> {
        const reset = updateEntity(this.task, 'DateDue', date);
        const keys = await this.scheduleHelperService.dueDateChanged(this.appointment.taskInstanceKeys, date, this.COMPONENT_LOG_TAG, reset);
        await this.taskInstanceService.fetchTasks(keys ?? this.appointment.taskInstanceKeys);
    }

    // Status
    async taskStatusChanged(key: number): Promise<void> {
        this.loading = true;
        const reset = updateEntity(this.task, 'C_TaskStatus_key', key);

        try {
            const keys = await this.taskInstanceService.taskBulkStatusChange({
                TaskInstanceKeys: this.appointment.taskInstanceKeys,
                TaskStatusKey: key ?? null,
                ModifiedBy: this.authService.getCurrentUserName(),
                ResourceKey: this.currentResourceKey,
                source: this.COMPONENT_LOG_TAG,
            });

            await this.taskInstanceService.fetchTasks(keys ?? this.appointment.taskInstanceKeys);
        } catch {
            reset();
        } finally {
            this.loading = false;
        }
    }

    assignedToChanged(newResourceKey: number | null): void {
        for (const taskInstance of this.appointment.taskInstances) {
            taskInstance.C_AssignedTo_key = newResourceKey;
        }
    }

    durationToChanged(newDuration: number | null): void {
        for (const taskInstance of this.appointment.taskInstances) {
            taskInstance.Duration = newDuration;
        }
    }
}
